import { uniqueId } from 'lodash';

import { stubDefaultCheck } from 'dashboard/apps/InquiryTemplateEditor/forms/utils';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  DatabaseAddressAutocompleteMethod,
  Disclaimer,
  GovernmentIdCaptureFileType,
  GovernmentIdEnabledIdClassByCountries,
  GovernmentIdVideoCaptureMethod,
  InputField,
  MatchResult,
  PhoneNumberInfoMatch,
  SelfieMatch,
  VerificationTemplateVersion,
  VerificationTemplateVersionAamva,
  VerificationTemplateVersionAamvaAttributes,
  VerificationTemplateVersionAttributes,
  VerificationTemplateVersionConfigAamva,
  VerificationTemplateVersionConfigDatabase,
  VerificationTemplateVersionConfigDatabaseBusiness,
  VerificationTemplateVersionConfigDatabaseBusinessFootprint,
  VerificationTemplateVersionConfigDatabasePhoneCarrier,
  VerificationTemplateVersionConfigDatabaseSerpro,
  VerificationTemplateVersionConfigDatabaseTin,
  VerificationTemplateVersionConfigDocument,
  VerificationTemplateVersionConfigEmailAddress,
  VerificationTemplateVersionConfigGovernmentId,
  VerificationTemplateVersionConfigPhoneNumber,
  VerificationTemplateVersionConfigSelfie,
  VerificationTemplateVersionDatabase,
  VerificationTemplateVersionDatabaseAttributes,
  VerificationTemplateVersionDatabaseBusiness,
  VerificationTemplateVersionDatabaseBusinessAttributes,
  VerificationTemplateVersionDatabaseBusinessFootprint,
  VerificationTemplateVersionDatabaseBusinessFootprintAttributes,
  VerificationTemplateVersionDatabasePhoneCarrier,
  VerificationTemplateVersionDatabasePhoneCarrierAttributes,
  VerificationTemplateVersionDatabaseSerpro,
  VerificationTemplateVersionDatabaseSerproAttributes,
  VerificationTemplateVersionDatabaseTin,
  VerificationTemplateVersionDatabaseTinAttributes,
  VerificationTemplateVersionDocument,
  VerificationTemplateVersionDocumentAttributes,
  VerificationTemplateVersionEmailAddress,
  VerificationTemplateVersionEmailAddressAttributes,
  VerificationTemplateVersionGovernmentId,
  VerificationTemplateVersionGovernmentIdAttributes,
  VerificationTemplateVersionPhoneNumber,
  VerificationTemplateVersionPhoneNumberAttributes,
  VerificationTemplateVersionRelationships,
  VerificationTemplateVersionSelfie,
  VerificationTemplateVersionSelfieAttributes,
  VerificationTemplateVersionStatus,
} from 'dashboard/models/verification-template-version';
import { FileType } from 'lib/file';
import { ApiModel } from 'lib/interfaces/api';
import { DocumentAllowedFileType } from 'lib/interfaces/StepsConfig';
import { SelfieCaptureOption, SelfieVideoCaptureMethod } from 'lib/interfaces/verificationConfig';
import { DeepPartial } from 'lib/types';
import { getChecks } from 'lib/verificationCheck';

export function createMockVerificationChecksForType({
  type,
  enabled = true,
  required = false,
}: {
  type: VerificationTemplateVersion['type'];
  enabled?: boolean;
  required?: boolean;
}): VerificationCheckConfig[] {
  return getChecks(type).map((check) => stubDefaultCheck({ check, required, enabled }));
}

// Default values taken from app/models/verification_template_version/*.rb
export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_AAMVA: VerificationTemplateVersionConfigAamva =
  {
    'verification-checks': [],
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE: VerificationTemplateVersionConfigDatabase =
  {
    'accepted-match-results': {
      name: MatchResult.Partial,
      birthdate: MatchResult.Partial,
      address: MatchResult.Partial,
      'phone-number': MatchResult.None,
      'social-security-number': MatchResult.None,
    },
    'address-autocomplete-method': DatabaseAddressAutocompleteMethod.GoogleMaps,
    'input-fields': [InputField.Name, InputField.Birthdate, InputField.Address],
    'verification-checks': [],
    'vendor-configurations': {},
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_BUSINESS: VerificationTemplateVersionConfigDatabaseBusiness =
  {
    'verification-checks': [],
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_BUSINESS_FOOTPRINT: VerificationTemplateVersionConfigDatabaseBusinessFootprint =
  {
    'verification-checks': [],
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_PHONE_CARRIER: VerificationTemplateVersionConfigDatabasePhoneCarrier =
  {
    'verification-checks': [],
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_SERPRO: VerificationTemplateVersionConfigDatabaseSerpro =
  {
    'verification-checks': [],
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_TIN: VerificationTemplateVersionConfigDatabaseTin =
  {
    'verification-checks': [],
  };

export const DEFAULT_DOCUMENT_ALLOWED_IMAGES: DocumentAllowedFileType[] = [
  FileType.ImageHeic,
  FileType.ImageHeif,
  FileType.ImageJpeg,
  FileType.ImageJpg,
  FileType.ImagePng,
  FileType.ImageTif,
  FileType.ImageTiff,
];

export const DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES: DocumentAllowedFileType[] = [
  FileType.ApplicationPdf,
  FileType.ImageHeic,
  FileType.ImageHeif,
  FileType.ImageJpeg,
  FileType.ImageJpg,
  FileType.ImagePng,
  FileType.ImageTif,
  FileType.ImageTiff,
];

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DOCUMENT: VerificationTemplateVersionConfigDocument =
  {
    classifications: [],
    extractions: [],
    insights: [],
    'insight-limit': 5,
    'enabled-file-types': DEFAULT_DOCUMENT_ALLOWED_FILE_TYPES,
    'verification-checks': [],
    'document-kind': 'generic',
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_EMAIL_ADDRESS: VerificationTemplateVersionConfigEmailAddress =
  {
    'verification-checks': [],
    'num-attempts': 5,
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_GOVERNMENT_ID: VerificationTemplateVersionConfigGovernmentId =
  {
    'accepted-match-results': {
      name: MatchResult.Partial,
      birthdate: MatchResult.Partial,
      address: MatchResult.Partial,
      'phone-number': MatchResult.None,
      'social-security-number': MatchResult.None,
    },
    // expect server to autogenerate this
    'enabled-id-classes-by-country': {} as GovernmentIdEnabledIdClassByCountries,
    'enabled-skip-unknown': false,
    'external-disabled-feature-configs': [],
    'scanner-skip-features': [],
    'verification-checks': [],
    disclaimer: Disclaimer.None,
    'device-handoff-options': [],
    'enabled-capture-file-types': [GovernmentIdCaptureFileType.Image],
    'enabled-file-types': [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/heic',
      'image/heif',
      'image/tif',
      'image/tiff',
      'application/pdf',
    ],
    'enabled-features': [],
    'video-capture-methods': [GovernmentIdVideoCaptureMethod.Stream],
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_PHONE_NUMBER: VerificationTemplateVersionConfigPhoneNumber =
  {
    'require-info-match': PhoneNumberInfoMatch.Name,
    'enabled-signals': [],
    'verification-checks': [],
    'num-attempts': 5,
  };

export const DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_SELFIE: VerificationTemplateVersionConfigSelfie =
  {
    'require-selfie-match': SelfieMatch.ProfileAndCenter,
    'enabled-capture-options-desktop': [
      SelfieCaptureOption.WebCamera,
      SelfieCaptureOption.DeviceHandoff,
    ],
    'enabled-capture-options-mobile': [SelfieCaptureOption.WebCamera, SelfieCaptureOption.Stillfie],
    'external-disabled-feature-configs': [],
    'verification-checks': [],
    'require-strict-selfie-capture': false,
    'enabled-capture-file-types': ['video'],
    'video-capture-methods': [SelfieVideoCaptureMethod.Stream],
  };

export const generateVerificationTemplateVersionId = () => uniqueId('vtmplv_');

export function createMockVerificationTemplateVersionAamva(
  attributes?: Partial<VerificationTemplateVersionAamvaAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionAamva {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionAamva,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_AAMVA,
      status: VerificationTemplateVersionStatus.Published,
      name: 'AAMVA Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionAamva,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionDatabase(
  attributes?: Partial<VerificationTemplateVersionDatabaseAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionDatabase {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionDatabase,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Database Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionDatabase,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionDatabaseBusiness(
  attributes?: Partial<VerificationTemplateVersionDatabaseBusinessAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionDatabaseBusiness {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionDatabaseBusiness,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_BUSINESS,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Database Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionDatabaseBusiness,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionDatabaseBusinessFootprint(
  attributes?: Partial<VerificationTemplateVersionDatabaseBusinessFootprintAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionDatabaseBusinessFootprint {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionDatabaseBusinessFootprint,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_BUSINESS_FOOTPRINT,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Database Business Footprint Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionDatabaseBusinessFootprint,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionDatabasePhoneCarrier(
  attributes?: Partial<VerificationTemplateVersionDatabasePhoneCarrierAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionDatabasePhoneCarrier {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionDatabasePhoneCarrier,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_PHONE_CARRIER,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Database Phone Carrier Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionDatabasePhoneCarrier,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionDatabaseSerpro(
  attributes?: Partial<VerificationTemplateVersionDatabaseSerproAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionDatabaseSerpro {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionDatabaseSerpro,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_SERPRO,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Database Serpro Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionDatabaseSerpro,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionDatabaseTin(
  attributes?: Partial<VerificationTemplateVersionDatabaseTinAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionDatabaseTin {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionDatabaseTin,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DATABASE_TIN,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Database TIN Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionDatabaseTin,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionDocument(
  attributes?: Partial<VerificationTemplateVersionDocumentAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionDocument {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionDocument,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_DOCUMENT,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Document Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionDocument,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      'verification-template': {
        data: { id: 'vtmpl_doc123', type: ApiModel.VerificationTemplate },
      },
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionEmailAddress(
  attributes?: Partial<VerificationTemplateVersionEmailAddressAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionEmailAddress {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionEmailAddress,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_EMAIL_ADDRESS,
      status: VerificationTemplateVersionStatus.Published,
      name: 'EmailAddress Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionEmailAddress,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionGovernmentId(
  attributes?: Partial<VerificationTemplateVersionGovernmentIdAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionGovernmentId {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionGovernmentId,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_GOVERNMENT_ID,
      status: VerificationTemplateVersionStatus.Published,
      name: 'GovernmentId Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionGovernmentId,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      'verification-template': {
        data: { id: 'vtmpl_gov123', type: ApiModel.VerificationTemplate },
      },
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionPhoneNumber(
  attributes?: Partial<VerificationTemplateVersionPhoneNumberAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionPhoneNumber {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionPhoneNumber,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_PHONE_NUMBER,
      status: VerificationTemplateVersionStatus.Published,
      name: 'PhoneNumber Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionPhoneNumber,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'verification-template': undefined,
      ...relationships,
    },
  };
}

export function createMockVerificationTemplateVersionSelfie(
  attributes?: Partial<VerificationTemplateVersionSelfieAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
  id?: string,
): VerificationTemplateVersionSelfie {
  return {
    id: id || generateVerificationTemplateVersionId(),
    type: ApiModel.VerificationTemplateVersionSelfie,
    attributes: {
      ...DEFAULT_VERIFICATION_TEMPLATE_VERSION_CONFIG_SELFIE,
      status: VerificationTemplateVersionStatus.Published,
      name: 'Selfie Verification',
      'verification-checks': createMockVerificationChecksForType({
        type: ApiModel.VerificationTemplateVersionSelfie,
      }),
      'created-at': '2020-02-15T06:31:58.000Z',
      'updated-at': '2020-02-15T09:31:58.000Z',
      ...attributes,
    },
    relationships: {
      'verification-template': {
        data: { id: 'vtmpl_selfie123', type: ApiModel.VerificationTemplate },
      },
      ...relationships,
    },
  };
}

// generic vtmplv stub used when adding vtmplvs from inserted module
export function createMockVerificationTemplateVersion(
  id: string,
  type: VerificationTemplateVersion['type'],
  name: string,
  attributes?: Partial<VerificationTemplateVersionAttributes>,
  relationships?: Partial<VerificationTemplateVersionRelationships>,
): DeepPartial<VerificationTemplateVersion> {
  return {
    id,
    type,
    attributes: {
      name,
      status: VerificationTemplateVersionStatus.Draft,
      ...attributes,
    },
    relationships: {
      'verification-template': {
        data: { id: undefined, type: ApiModel.VerificationTemplate },
      },
      ...relationships,
    },
  };
}
